import { supabase } from '../../shared/supabase/supabaseClient';
import type { Vehicle, VehicleDraft, VehicleListItem, VehicleSearchQuery } from '../domain/types';

type VehicleRow = {
  id: string;
  organization_id: string;
  customer_id: string | null;
  plate: string | null;
  brand: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  color: string | null;
  mileage: number | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

function mapRow(row: VehicleRow): Vehicle {
  return {
    id: row.id,
    organizationId: row.organization_id,
    customerId: row.customer_id,
    plate: row.plate,
    brand: row.brand,
    model: row.model,
    year: row.year,
    vin: row.vin,
    color: row.color,
    mileage: row.mileage,
    notes: row.notes,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapListItem(row: VehicleRow): VehicleListItem {
  return {
    id: row.id,
    customerId: row.customer_id,
    plate: row.plate,
    brand: row.brand,
    model: row.model,
    year: row.year,
    vin: row.vin,
    color: row.color,
    mileage: row.mileage,
    notes: row.notes,
    isActive: row.is_active,
  };
}

export async function listVehicles(
  organizationId: string,
  query: VehicleSearchQuery = {},
): Promise<VehicleListItem[]> {
  const { data, error } = await supabase.rpc('list_vehicles', {
    p_organization_id: organizationId,
    p_customer_id: query.customerId ?? null,
    p_search: query.search ?? null,
  });

  if (error) throw new Error(error.message);
  return ((data ?? []) as VehicleRow[]).map(mapListItem);
}

export async function getVehicle(id: string): Promise<Vehicle | null> {
  const { data, error } = await supabase.rpc('get_vehicle', { p_id: id });
  if (error) throw new Error(error.message);
  if (!data || (data as VehicleRow[]).length === 0) return null;
  return mapRow((data as VehicleRow[])[0]!);
}

export async function upsertVehicle(
  organizationId: string,
  draft: VehicleDraft,
): Promise<string> {
  const { data, error } = await supabase.rpc('upsert_vehicle', {
    p_id: draft.id ?? null,
    p_organization_id: organizationId,
    p_customer_id: draft.customerId || null,
    p_plate: draft.plate.trim() || null,
    p_brand: draft.brand.trim() || null,
    p_model: draft.model.trim() || null,
    p_year: draft.year.trim() ? Number(draft.year) : null,
    p_vin: draft.vin.trim() || null,
    p_color: draft.color.trim() || null,
    p_mileage: draft.mileage.trim() ? Number(draft.mileage) : null,
    p_notes: draft.notes.trim() || null,
  });

  if (error) throw new Error(error.message);
  const vehicleId = data as string | null;
  if (!vehicleId) throw new Error('No se pudo guardar el vehículo.');
  return vehicleId;
}

export async function deactivateVehicle(id: string): Promise<void> {
  const { error } = await supabase.rpc('deactivate_vehicle', { p_id: id });
  if (error) throw new Error(error.message);
}
